import Image from 'next/image';
import Link from 'next/link';

import {
  useMediaQuery,
  Grid,
  Button,
  Card,
  CardMedia,
  CardContent,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import PropTypes from 'prop-types';
import { FormattedDate } from 'react-intl';

import { LearnMoreLink } from '@/src/components/atoms';
import { SectionHeader } from '@/src/components/molecules';
import { CardBase } from '@/src/components/organisms';

const useStyles = makeStyles(theme => ({
  textWhite: {
    color: 'white',
  },
  card: {
    display: 'flex',
    boxShadow: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'grid',
    },
  },
  cardMedia: {
    height: 300,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  cardHighlighted: {
    background: theme.palette.primary.dark,
  },
  imageWrapper: {
    position: 'relative',
    height: 300,
    width: 180,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  cta: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const News = props => {
  const { className, data, ...rest } = props;

  const classes = useStyles();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  return (
    <div className={className} {...rest}>
      <SectionHeader
        title="Estamos na mídia"
        subtitle="Veja o que os principais canais de notícias estão falando sobre nós."
        data-aos="fade-up"
      />
      <Grid container spacing={isMd ? 4 : 2}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={isMd ? 4 : 2} direction="column">
            {data.map((item, index) => (
              <Grid item xs={12} key={index.toString()} data-aos="fade-up">
                <Card className={classes.card}>
                  <CardMedia className={classes.cardMedia}>
                    <div className={classes.imageWrapper}>
                      <Image
                        src={item.data.cover?.url}
                        alt={item.data.title}
                        layout="fill"
                        objectFit="cover"
                        quality={50}
                      />
                    </div>
                  </CardMedia>
                  <CardContent className={classes.cardContent}>
                    <Typography variant="h6" component="div" color="textPrimary">
                      {item.data.title}
                    </Typography>
                    <Typography variant="body1" color="textPrimary">
                      {item.data.subtitle}
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                      <FormattedDate value={item.data.date} />
                    </Typography>
                    <div className={classes.cta}>
                      <Link href={item.data.link?.url} passHref>
                        <LearnMoreLink
                          title="leia mais"
                          rel="noopener noreferrer"
                          target="_blank"
                          typographyProps={{ variant: 'h6', component: 'div' }}
                        />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12} md={4}>
          <Grid container spacing={isMd ? 4 : 2} direction="column">
            <Grid item xs={12} data-aos="fade-up">
              <CardBase withShadow className={classes.cardHighlighted}>
                <SectionHeader
                  title={<span className={classes.textWhite}>Quer saber mais sobre nós?</span>}
                  titleVariant="h6"
                  subtitle={
                    <span className={classes.textWhite}>
                      Entre em contato com nossa equipe. Será um prazer conversar com você!
                    </span>
                  }
                  subtitleVariant="body1"
                  ctaGroup={[
                    <Link href="/contact" passHref key="contactCta">
                      <Button variant="contained" size="large" component="a" aria-label="Contato">
                        Contato
                      </Button>
                    </Link>,
                  ]}
                  disableGutter
                  align="left"
                />
              </CardBase>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

News.propTypes = {
  /**
   * External classes
   */
  className: PropTypes.string,
  /**
   * Data will be rendered
   */
  data: PropTypes.array,
};

export default News;
